import { ReactElement, useEffect, useState } from 'react';
import { useRouteLoaderData } from 'react-router-dom';

import { useHirePlanFormStepStore } from '../../../stores/useHirePlanFormStepStore';
import { useWindowSizeStore } from '../../../stores/useWindowSizeStore';
import { Button } from '../../Button';
import ExpandableList from '../../ExpandedList';
import { LeadingIcon } from '../../icons/LeadingIcon';
import { SideBar } from '../../SideBar';

interface Especiality {
  nomeEspecialidade: string;
  idEspecialidade: number;
  procedures: Procedure[];
}

interface Procedure {
  codigoEspecialidade: number;
  nomeProcedimento: string;
}

interface HirePlanFormSectionBaseProps {
  FormComponent: () => ReactElement;
  primaryRouteId?: string;
  secondaryRouteId?: string;
  maxWidthClassName: string;
  showCoverageButton?: boolean;
  enableProcedures?: boolean;
}

export function HirePlanFormSectionBase({
  FormComponent,
  primaryRouteId,
  secondaryRouteId,
  maxWidthClassName,
  showCoverageButton = true,
  enableProcedures = false,
}: HirePlanFormSectionBaseProps): ReactElement {
  const primaryRouteData = useRouteLoaderData(primaryRouteId ?? '');
  const secondaryRouteData = useRouteLoaderData(secondaryRouteId ?? '');
  const routeData = (primaryRouteData ?? secondaryRouteData) as any;

  const clearStep = useHirePlanFormStepStore(state => state.clearStep);
  const width = useWindowSizeStore(state => state.width);
  const [sideBarOpen, setSideBarOpen] = useState(false);
  const [especialities, setEspecialities] = useState<Especiality[]>([]);

  const buildEspecialityList = (listEspeciality: Especiality[]) => {
    listEspeciality.forEach(especiality => {
      especiality.procedures = [];
    });

    return listEspeciality;
  };

  const findEspeciality = (
    idEspecialidade: number,
  ): Especiality | undefined => {
    return especialities.find(esp => esp.idEspecialidade == idEspecialidade);
  };

  const buildListOfProcedures = (listProcedures: Procedure[]) => {
    listProcedures.forEach(proc => {
      const especiality = findEspeciality(proc.codigoEspecialidade);

      if (especiality) {
        especiality.procedures.push(proc);
      }
    });
  };

  const findEspecialities = async () => {
    const getEspecialities = await fetch(
      `${process.env.REACT_APP_GET_DENTISTS_SPECIALTY}?token=${
        process.env.REACT_APP_TOKEN_S4E
      }&idEstado=${6}&pagina=${1}`,
    ).then(response => response.json());

    setEspecialities(buildEspecialityList(getEspecialities.dataObject.dados));
  };

  const findProcedures = async () => {
    if (!routeData?.produtoComercial?.id_produto_s4_e_c) {
      return;
    }

    const getProcedures = await fetch(
      `${process.env.REACT_APP_GET_SPECIALTY_PLAN}?token=${
        process.env.REACT_APP_TOKEN_S4E
      }&idPlano=${
        routeData.produtoComercial.id_produto_s4_e_c
      }&pagina=${1}&limite=${100}`,
    ).then(response => response.json());

    buildListOfProcedures(getProcedures.dataObject.dados);
  };

  useEffect(() => {
    clearStep();
    findEspecialities();
  }, []);

  useEffect(() => {
    if (!enableProcedures || especialities.length === 0) {
      return;
    }

    findProcedures();
  }, [enableProcedures, especialities]);

  return (
    <section className={`${maxWidthClassName} w-full mb-64`}>
      {width >= 748 && (
        <div className="bg-[#F8F8F8] px-8 h-[88px] flex items-center justify-between rounded-t-xl border-b border-b-primary-container">
          <img
            src="/assets/logos/odontogroup-azul.png"
            alt=""
            className="2xl:h-[62px] h-[px]"
          />
          {showCoverageButton && (
            <Button
              text="Mostrar coberturas"
              intent="tertiary"
              Icon={LeadingIcon}
              onClick={() => setSideBarOpen(true)}
            />
          )}
          <SideBar setIsSideOpen={setSideBarOpen} slideOpen={sideBarOpen}>
            {especialities.map(especiality => {
              if (especiality.procedures.length > 0) {
                return (
                  <ExpandableList
                    key={especiality.idEspecialidade}
                    title={especiality.nomeEspecialidade}
                    contentList={especiality.procedures.map(
                      proc => proc.nomeProcedimento,
                    )}
                  />
                );
              }

              return null;
            })}
          </SideBar>
        </div>
      )}
      <FormComponent />
    </section>
  );
}
